import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ShowBase, Tab, useRecordContext } from "react-admin";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { Student } from "../providers/studentsProvider";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { Row } from "../misc/Row";
import { ProfileCard } from "./student/cards/ProfileCard";
import { Tabs } from "../misc/Tabs";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Box } from "@mui/material";
import { NotesAndDocuments } from "../lists/NotesAndDocuments";
import { RightCollapsibleSidebar } from "../misc/RightCollapsibleSidebar";
import { StudentBookedQuotes } from "./StudentBookedQuotes";
import { StudentOverviewTab2 } from "./studentDetails2/StudentOverviewTab2";
import { StudentDrivingLessonsTab2 } from "./studentDetails2/StudentDrivingLessonsTab2";
import { StudentTheoryTab } from "./student/StudentTheoryTab";
import { grants } from "../backoffice.access_control";
import { StudentAdminForm } from "./student/StudentAdminForm";
import { StudentInAppNotificationsList } from "./student/StudentInAppNotificationsList";
import { StudentInvoicesList } from "./student/StudentInvoicesList";
import { StudentStickyNotesList } from "./student/StudentStickyNotesList";
import { useBookedQuotes } from "../hooks/useBookedQuotes";

/** See https://autovio.atlassian.net/browse/APP-5633 */
export function StudentDetails2() {
  const student = useRecordContext<Student>();
  return (
    <div data-testid="StudentDetails2" style={{ minWidth: "1400px" }}>
      <ShowBase resource="students" id={student?.id}>
        <_StudentDetails2 />
      </ShowBase>
    </div>
  );
}

function _StudentDetails2() {
  const student = useRecordAndSwitchDrivingSchoolIfNecessary<Student>((it) => it.drivingSchoolId);

  if (!student) {
    return <LoadingIndicator />;
  }

  return <_StudentDetailsWithSelectedBookedQuote />;
}

function _StudentDetailsWithSelectedBookedQuote() {
  return (
    <Routes>
      {/*
      - bookedQuoteId is persisted in the URL, so we have shareable link pointing to the right booked quote.
      - It's added as a URL parameter to the path, because hash and query params are overridden by the react-admin List component (pagination and sorting state).
      */}
      <Route path="/booked-quotes/:bookedQuoteId/*" element={<_StudentDetailsContent />} />
      <Route path="*" element={<_SelectedBookedQuoteURLHandler />} />
    </Routes>
  );
}

// handles both student view and dunning dialog view
function _SelectedBookedQuoteURLHandler() {
  const student = useRecordContext<Student>();
  const { selectedBookedQuote } = useBookedQuotes(student);
  const { pathname } = useLocation();
  const isDunningView = pathname.includes("/dunning");
  // Backwards compatibility for old URLs with tabs paths (/students/:id/theoryLessons, etc...)
  const studentViewTabPrefixUrlChunk = isDunningView ? "/dunning" : `/students/${student.id}`;
  const studentViewTabPath = pathname.slice(
    pathname.indexOf(studentViewTabPrefixUrlChunk) + studentViewTabPrefixUrlChunk.length,
  ); // theoryLessons, invoices, admin, etc...
  return <Navigate to={`booked-quotes/${selectedBookedQuote.id}${studentViewTabPath}`} replace />;
}

function _StudentDetailsContent() {
  const student = useRecordContext<Student>();
  const trainingIncludesTheoryLessonsOrExam =
    student.bookedTrainings.some((training) => training.hasCompulsoryTheoryLessons) ||
    student.bookedTrainings.some((training) => training.includesTheoryExam);

  return (
    <Row gap={3} sx={{ containerType: "inline-size", containerName: "student-details-layout" }}>
      <ProfileCard trainingIncludesTheoryLessonsOrExam={trainingIncludesTheoryLessonsOrExam} />
      <Tabs
        sx={{ ...overviewCardStyle, padding: "20px 30px 10px 30px" }}
        style={{ flex: 2 }}
        header={<StudentBookedQuotes student={student} />}
        separateCards
      >
        <Tab label="Überblick" /* no path attribute here, so the tab is shown by default */>
          <StudentOverviewTab2
            student={student}
            trainingIncludesTheoryLessonsOrExam={trainingIncludesTheoryLessonsOrExam}
          />
        </Tab>
        <Tab label="Theorie" path="theoryLessons">
          <StudentTheoryTab />
        </Tab>
        <Tab label="Fahrstunden" path="drivingLessons">
          <StudentDrivingLessonsTab2 />
        </Tab>
        {grants.has("Invoice:listPerStudent") && (
          <Tab label="Abrechnung" path="invoices">
            <StudentInvoicesList />
          </Tab>
        )}
        {grants.has("admin") && (
          <Tab label="Admin" path="admin">
            <Row gap="15px">
              <StudentAdminForm />
              <Box sx={{ ...overviewCardStyle, flex: 1, minWidth: "530px" }}>
                <Tabs syncWithLocation={false} fullWidth>
                  <Tab label="Sticky Notes">
                    <StudentStickyNotesList />
                  </Tab>
                  <Tab label="In-App-Benachrichtigungen">
                    <StudentInAppNotificationsList />
                  </Tab>
                </Tabs>
              </Box>
            </Row>
          </Tab>
        )}
      </Tabs>
      <RightCollapsibleSidebar>
        <Box sx={{ borderRadius: "16px", overflow: "hidden" }}>
          <NotesAndDocuments source="student" />
        </Box>
      </RightCollapsibleSidebar>
    </Row>
  );
}
